/*
 * Minimog predictive search – Pen Tech v26.
 *
 * - Arabic: Shopify answers /ar/search/suggest with HTTP 417 "Unsupported buyer locale".
 *   For such locales (Arabic, or any locale that answered 417 once this session) the
 *   root /search/suggest is called instead; result links are rewritten to keep the
 *   /ar prefix and the few labels in the returned main-language HTML are translated.
 *   Theme setting: Search and filters > "Arabic live search fix" (on by default).
 * - Any failed request stops the spinner and shows "Press Enter to see all results".
 * - Search keywords (window.PTSearch.mapQuery from assets/pentech-search.js) and tight
 *   search fields (title, product type, vendor, tag, SKU by default).
 * - Older responses that arrive late are dropped; pending requests are aborted.
 */
(function () {
  "use strict";
  if (!window.customElements || window.customElements.get("predictive-search")) return;

  var DEFAULT_FIELDS = "title,product_type,vendor,tag,variants.sku";
  var ALLOWED_FIELDS = ["title", "product_type", "vendor", "tag", "variants.sku", "variants.title", "variants.barcode", "body", "author"];
  var ROOT_LOCALES = ["ar"];
  var STORE_KEY = "pt-ps-root:";

  function ptConfig() {
    if (window.PTSearch && typeof window.PTSearch.config === "function") return window.PTSearch.config() || {};
    var el = document.getElementById("pt-search-config");
    if (!el) return {};
    try {
      return JSON.parse(el.textContent) || {};
    } catch (e) {
      return {};
    }
  }

  function ptText(key, fallback) {
    var value = "";
    if (window.PTSearch && typeof window.PTSearch.i18n === "function") value = window.PTSearch.i18n(key);
    else value = (ptConfig().i18n || {})[key] || "";
    return value || fallback || "";
  }

  function ptMap(query) {
    var q = String(query || "").replace(/\s+/g, " ").trim();
    if (window.PTSearch && typeof window.PTSearch.mapQuery === "function") return window.PTSearch.mapQuery(q) || q;
    return q;
  }

  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();

      this.selectors = {
        searchQuery: "[data-query]",
        searchMessage: "[data-message]",
        input: "[data-search-input]",
        submit: 'button[type="submit"]',
        loading: "[data-spinner]",
        clear: "[data-clear-search]",
        popularSearchItems: ["[data-ps-item]"],
        moreResultIcon: "[data-more-result-icon]",
      };

      this.container = document.querySelector("[data-search-popup]") || this;
      this.domNodes = typeof queryDomNodes === "function" ? queryDomNodes(this.selectors, this.container) : {};

      this.cachedResults = {};
      this.transitionDuration = 300;
      this.requestId = 0;
      this.controller = null;
      this.input = this.querySelector('input[type="search"]');
      this.predictiveSearchResults = this.querySelector("[data-predictive-search]");
      this.searchPopupSearults = this.querySelector(".m-search-popup__result");
      this.popularSearches = this.querySelector("[data-popular-searches]");
      this.searchCount = this.querySelector("[data-search-count]");
      this.statusRegion = this.querySelector("[data-pt-ps-status]");

      if (this.input && this.predictiveSearchResults && this.searchPopupSearults) this.setupEventListeners();
    }

    setPopularSearchesLink() {
      var items = this.domNodes.popularSearchItems || [];
      items.forEach(function (itm) {
        var q = itm.dataset.psQuery;
        if (window.PTSearch && typeof window.PTSearch.searchUrl === "function") itm.href = window.PTSearch.searchUrl(q, { type: "product" });
        else if (typeof createSearchLink === "function") itm.href = createSearchLink(q);
      });
    }

    setupEventListeners() {
      var clear = this.domNodes.clear;
      var form = this.querySelector("form.m-search-form");
      if (form) form.addEventListener("submit", this.onFormSubmit.bind(this));

      var onChange = this.onChange.bind(this);
      this.input.addEventListener("input", typeof debounce === "function" ? debounce(onChange, 300) : onChange);

      if (clear) clear.addEventListener("click", this.onClearSearch.bind(this));
      if (window.MinimogEvents && window.MinimogTheme && window.MinimogTheme.pubSubEvents) {
        window.MinimogEvents.subscribe(window.MinimogTheme.pubSubEvents.closeSearchPopup, () => this.close(true));
      }
    }

    getQuery() {
      return this.input.value.trim();
    }

    onChange() {
      var searchTerm = this.getQuery();

      if (!searchTerm.length) {
        this.close(true);
        return;
      }

      this.getSearchResults(searchTerm);
    }

    onFormSubmit(event) {
      if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
    }

    onClearSearch(event) {
      event.preventDefault();
      this.input.value = "";
      this.onChange();
      this.input.focus();
    }

    /* ---------- request ---------- */

    getFields() {
      var raw = this.dataset.searchFields || ptConfig().fields || DEFAULT_FIELDS;
      var seen = [];
      String(raw)
        .toLowerCase()
        .split(",")
        .forEach(function (f) {
          f = f.trim();
          if (f && ALLOWED_FIELDS.indexOf(f) > -1 && seen.indexOf(f) === -1) seen.push(f);
        });
      if (this.dataset.searchByBody === "true" && seen.indexOf("body") === -1) seen.push("body");
      return (seen.length ? seen : DEFAULT_FIELDS.split(",")).join(",");
    }

    getUnavailable() {
      var u = String(this.dataset.unavailableProductsOption || "last").toLowerCase();
      return ["show", "hide", "last"].indexOf(u) > -1 ? u : "last";
    }

    localePrefix() {
      var root =
        (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
        (window.MinimogSettings && window.MinimogSettings.routes && window.MinimogSettings.routes.root) ||
        "/";
      return String(root).replace(/\/+$/, "");
    }

    localeCode() {
      var s = window.MinimogSettings && window.MinimogSettings.shop_locale;
      return String((s && s.current) || document.documentElement.lang || "").toLowerCase();
    }

    rootFallbackOn() {
      return ptConfig().rootFallback !== false;
    }

    shouldUseRoot() {
      if (!this.rootFallbackOn() || !this.localePrefix()) return false;
      var code = this.localeCode();
      var list = (ptConfig().rootLocales || ROOT_LOCALES).map(function (c) {
        return String(c).toLowerCase();
      });
      if (list.indexOf(code) > -1 || list.indexOf(code.split("-")[0]) > -1) return true;
      try {
        return window.sessionStorage.getItem(STORE_KEY + code) === "1";
      } catch (e) {
        return false;
      }
    }

    rememberRoot() {
      try {
        window.sessionStorage.setItem(STORE_KEY + this.localeCode(), "1");
      } catch (e) {
        /* private mode – fine, we just retry next time */
      }
    }

    localSuggestUrl() {
      var routes = window.MinimogSettings && window.MinimogSettings.routes;
      return (routes && routes.predictive_search_url) || this.localePrefix() + "/search/suggest";
    }

    rootSuggestUrl() {
      var local = this.localSuggestUrl();
      var prefix = this.localePrefix();
      if (prefix && local.indexOf(prefix + "/") === 0) return local.slice(prefix.length);
      return "/search/suggest";
    }

    buildUrl(base, term) {
      var params = new URLSearchParams();
      params.set("q", term);
      params.set("resources[options][unavailable_products]", this.getUnavailable());
      params.set("resources[options][fields]", this.getFields());
      params.set("section_id", "predictive-search");
      return base + "?" + params.toString();
    }

    fetchSuggest(url) {
      var options = { credentials: "same-origin" };
      if (this.controller) options.signal = this.controller.signal;
      return fetch(url, options);
    }

    getSearchResults(searchTerm) {
      var term = ptMap(searchTerm);
      var queryKey = term.toLowerCase().replace(/\s+/g, "-");
      var requestId = ++this.requestId;

      if (this.controller) this.controller.abort();
      this.controller = null;

      this.toggleClearSearch(false);

      if (this.cachedResults[queryKey]) {
        this.renderSearchResults(this.cachedResults[queryKey]);
        return;
      }

      this.toggleSpinnerLoading(true);
      if (typeof AbortController === "function") this.controller = new AbortController();

      var useRoot = this.shouldUseRoot();
      var firstUrl = this.buildUrl(useRoot ? this.rootSuggestUrl() : this.localSuggestUrl(), term);

      this.fetchSuggest(firstUrl)
        .then((response) => {
          // 417 "Unsupported buyer locale": ask the main-language endpoint instead
          if (response.status === 417 && !useRoot && this.rootFallbackOn() && this.localePrefix()) {
            this.rememberRoot();
            return this.fetchSuggest(this.buildUrl(this.rootSuggestUrl(), term)).then(function (r) {
              return { response: r, root: true };
            });
          }
          return { response: response, root: useRoot };
        })
        .then(function (result) {
          if (!result.response.ok) throw new Error(String(result.response.status));
          return result.response.text().then(function (text) {
            return { text: text, root: result.root };
          });
        })
        .then((result) => {
          if (requestId !== this.requestId) return;
          var section = new DOMParser().parseFromString(result.text, "text/html").querySelector("#shopify-section-predictive-search");
          if (!section) throw new Error("predictive-search section missing");
          if (result.root) this.localizeMarkup(section);
          var resultsMarkup = section.innerHTML;
          this.cachedResults[queryKey] = resultsMarkup;
          this.renderSearchResults(resultsMarkup);
        })
        .catch((error) => {
          if (error && error.name === "AbortError") return;
          if (requestId !== this.requestId) return;
          this.renderError();
        });
    }

    // Main-language results shown on /ar: keep links on /ar and translate the labels
    localizeMarkup(root) {
      var prefix = this.localePrefix();
      if (prefix) {
        root.querySelectorAll("a[href]").forEach(function (a) {
          var href = a.getAttribute("href") || "";
          if (href.charAt(0) !== "/" || href.charAt(1) === "/") return;
          if (href === prefix || href.indexOf(prefix + "/") === 0 || href.indexOf(prefix + "?") === 0) return;
          a.setAttribute("href", prefix + href);
        });
      }

      root.querySelectorAll("[data-pt-i18n]").forEach(function (el) {
        var text = ptText(el.getAttribute("data-pt-i18n"));
        if (text) el.textContent = text;
      });

      // screen-reader price labels printed by snippets/product-prices.liquid
      var regular = ptText("regular_price");
      var sale = ptText("sale_price");
      root.querySelectorAll(".m-price__regular > .m\\:visually-hidden").forEach(function (el) {
        if (regular) el.textContent = regular;
      });
      root.querySelectorAll(".m-price__sale").forEach(function (box) {
        var hidden = box.querySelectorAll(":scope > .m\\:visually-hidden");
        if (hidden[0] && sale) hidden[0].textContent = sale;
        if (hidden[1] && regular) hidden[1].textContent = regular;
      });
    }

    /* ---------- render ---------- */

    announce(text) {
      if (this.statusRegion) this.statusRegion.textContent = text || "";
    }

    renderSearchResults(resultsMarkup) {
      this.announce("");
      this.predictiveSearchResults.innerHTML = resultsMarkup;
      this.setAttribute("results", true);
      var searchItemsWrapper = this.querySelector("[data-search-items-wrapper]");
      this.renderSearchQueryAndMessage(!!(searchItemsWrapper && searchItemsWrapper.childElementCount > 0));

      this.toggleSpinnerLoading(false);
      this.toggleClearSearch(true);
      this.open();
    }

    renderError() {
      this.toggleSpinnerLoading(false);
      this.toggleClearSearch(true);
      var message = document.createElement("p");
      message.className = "pt-ps-msg";
      message.textContent = ptText("press_enter", "Press Enter to see all results");
      this.announce(message.textContent);
      this.predictiveSearchResults.innerHTML = "";
      this.predictiveSearchResults.appendChild(message);
      this.setAttribute("results", true);
      this.renderSearchQueryAndMessage(false);
      this.open();
    }

    renderSearchQueryAndMessage(results) {
      var searchQuery = this.domNodes.searchQuery;
      var searchMessage = this.domNodes.searchMessage;
      var moreResultIcon = this.domNodes.moreResultIcon;

      if (searchQuery) searchQuery.textContent = this.input.value;

      this.predictiveSearchResults.classList.remove("m:hidden");
      this.searchPopupSearults.classList.remove("m:hidden");

      if (searchMessage) searchMessage.textContent = searchMessage.dataset.resultsTitle || "";
      if (moreResultIcon) moreResultIcon.classList.toggle("m:hidden", !!results);
    }

    toggleSpinnerLoading(show) {
      var loading = this.domNodes.loading;
      var submit = this.domNodes.submit;
      if (submit) submit.style.visibility = show ? "hidden" : "visible";
      if (loading) loading.style.visibility = show ? "visible" : "hidden";
      this.predictiveSearchResults.setAttribute("aria-busy", show ? "true" : "false");
    }

    toggleClearSearch(show) {
      var clear = this.domNodes.clear;
      if (clear) clear.style.visibility = show ? "visible" : "hidden";
    }

    open() {
      this.searchPopupSearults.style.setProperty(
        "--search-result-max-height",
        (this.resultsMaxHeight || this.getResultsMaxHeight()) + "px"
      );
      this.setAttribute("open", true);
      this.input.setAttribute("aria-expanded", true);
      this.isOpen = true;

      if (this.popularSearches) this.popularSearches.classList.add("m:hidden");
      if (this.searchCount) this.searchCount.classList.remove("m:hidden");
    }

    close(clearSearchTerm) {
      // drop anything still loading so the spinner never sticks
      this.requestId += 1;
      if (this.controller) this.controller.abort();
      this.controller = null;
      this.toggleSpinnerLoading(false);

      if (clearSearchTerm) {
        this.input.value = "";
        this.removeAttribute("results");
        this.toggleClearSearch(false);
        this.resultsMaxHeight = false;
      }

      this.removeAttribute("open");
      this.input.setAttribute("aria-expanded", false);
      this.isOpen = false;
      this.predictiveSearchResults.classList.add("m:hidden");
      this.searchPopupSearults.classList.add("m:hidden");
      if (this.popularSearches) this.popularSearches.classList.remove("m:hidden");
      if (this.searchCount) this.searchCount.classList.add("m:hidden");
    }

    getResultsMaxHeight() {
      var el = document.querySelector("predictive-search") || this;
      this.resultsMaxHeight = window.innerHeight - el.getBoundingClientRect().bottom;
      return this.resultsMaxHeight;
    }
  }

  customElements.define("predictive-search", PredictiveSearch);
})();
